import { Banner } from '@/lib/banners/banners'
import { AssetType, SearchPath } from '@/lib/assets/types'
import { getData } from '@/lib/categories/data'

export type MainCategory = {
  id: string
  name: string
  slug: string
  searchPath: SearchPath
  image?: string | null
  categories?: Category[]
  banner?: Banner
  // SEO
  title?: string
  description?: string
  keywords?: string[]
}

export type Category = {
  id: string
  mainCategoryId: string
  mainCategorySlug: string
  name: string
  slug: string
  image?: string | null
  icon?: string | null
  // The type of asset that this category contains.
  assetType: AssetType
  subCategories?: SubCategory[]
  // SEO
  title?: string
  description?: string
  keywords?: string[]
}

export type SubCategory = {
  id: string
  mainCategoryId: string
  mainCategorySlug: string
  categoryId: string
  categorySlug: string
  name: string
  slug: string
  image?: string | null
  contents?: Content[]
  // SEO
  title?: string
  description?: string
  keywords?: string[]
}

export type Content = {
  id: string
  mainCategoryId: string
  mainCategorySlug: string
  categoryId: string
  categorySlug: string
  subCategoryId: string
  subCategorySlug: string
  name: string
  slug: string
  image?: string | null
  // The tag used to searchQuery for this content in the Legacy Crate API. Takes precedence over searchQuery.
  searchTag?: string
  // The query used to searchQuery for this content in the Legacy Crate API.
  searchQuery?: string
  // The default broadness of the searchQuery for this content in the Legacy Crate API.
  searchBroadness?: number
  // The minimum broadness of the searchQuery for this content in the Legacy Crate API.
  minSearchBroadness?: number
  // The searchQuery category of this content in the Legacy Crate API.
  searchCategory?: string
  // SEO
  title?: string
  description?: string
  keywords?: string[]
}

export type GetCategoriesFilter = {
  mainCategoryIdOrSlug?: string | null
}

export type GetSubCategoriesFilter = GetCategoriesFilter & {
  categoryIdOrSlug?: string | null
}

export type GetContentsFilter = GetSubCategoriesFilter & {
  subCategoryIdOrSlug?: string | null
}

export async function getMainCategories(): Promise<MainCategory[]> {
  return (await getData()).mainCategories
}

export async function getMainCategoryByIdOrSlug(idOrSlug: string): Promise<MainCategory | null> {
  const mainCategory = (await getData()).mainCategories.find((v) => v.id === idOrSlug || v.slug === idOrSlug)

  return mainCategory
    ? {
        id: mainCategory.id,
        name: mainCategory.name,
        slug: mainCategory.slug,
        searchPath: mainCategory.searchPath,
        banner: mainCategory.banner,
        image: mainCategory.image,
        title: mainCategory.title,
        description: mainCategory.description,
        keywords: mainCategory.keywords,
      }
    : null
}

export async function getCategories({ mainCategoryIdOrSlug }: GetCategoriesFilter = {}): Promise<Category[]> {
  return (await getData()).categories.filter(
    (c) =>
      !mainCategoryIdOrSlug || c.mainCategoryId === mainCategoryIdOrSlug || c.mainCategorySlug === mainCategoryIdOrSlug
  )
}

export async function getCategoryByIdOrSlug(
  idOrSlug: string,
  { mainCategoryIdOrSlug }: GetCategoriesFilter = {}
): Promise<Category | null> {
  return (
    (await getData()).categories.find(
      (c) =>
        (c.id === idOrSlug || c.slug === idOrSlug) &&
        (!mainCategoryIdOrSlug ||
          c.mainCategoryId === mainCategoryIdOrSlug ||
          c.mainCategorySlug === mainCategoryIdOrSlug)
    ) ?? null
  )
}

export async function getSubCategories({
  mainCategoryIdOrSlug,
  categoryIdOrSlug,
}: GetSubCategoriesFilter = {}): Promise<SubCategory[]> {
  return (await getData()).subCategories.filter(
    (c) =>
      (!mainCategoryIdOrSlug ||
        c.mainCategoryId === mainCategoryIdOrSlug ||
        c.mainCategorySlug === mainCategoryIdOrSlug) &&
      (!categoryIdOrSlug || c.categoryId === categoryIdOrSlug || c.categorySlug === categoryIdOrSlug)
  )
}

export async function getSubCategoryByIdOrSlug(
  idOrSlug: string,
  { mainCategoryIdOrSlug, categoryIdOrSlug }: GetSubCategoriesFilter = {}
): Promise<SubCategory | null> {
  return (
    (await getData()).subCategories.find(
      (c) =>
        (c.id === idOrSlug || c.slug === idOrSlug) &&
        (!mainCategoryIdOrSlug ||
          c.mainCategoryId === mainCategoryIdOrSlug ||
          c.mainCategorySlug === mainCategoryIdOrSlug) &&
        (!categoryIdOrSlug || c.categoryId === categoryIdOrSlug || c.categorySlug === categoryIdOrSlug)
    ) ?? null
  )
}

export async function getContents({
  mainCategoryIdOrSlug,
  categoryIdOrSlug,
  subCategoryIdOrSlug,
}: GetContentsFilter = {}): Promise<Content[]> {
  return (await getData()).contents.filter(
    (c) =>
      (!mainCategoryIdOrSlug ||
        c.mainCategoryId === mainCategoryIdOrSlug ||
        c.mainCategorySlug === mainCategoryIdOrSlug) &&
      (!categoryIdOrSlug || c.categoryId === categoryIdOrSlug || c.categorySlug === categoryIdOrSlug) &&
      (!subCategoryIdOrSlug || c.subCategoryId === subCategoryIdOrSlug || c.subCategorySlug === subCategoryIdOrSlug)
  )
}

export async function getContentByIdOrSlug(
  idOrSlug: string,
  { mainCategoryIdOrSlug, categoryIdOrSlug, subCategoryIdOrSlug }: GetContentsFilter = {}
): Promise<Content | null> {
  return (
    (await getData()).contents.find(
      (c) =>
        (c.id === idOrSlug || c.slug === idOrSlug) &&
        (!mainCategoryIdOrSlug ||
          c.mainCategoryId === mainCategoryIdOrSlug ||
          c.mainCategorySlug === mainCategoryIdOrSlug) &&
        (!categoryIdOrSlug || c.categoryId === categoryIdOrSlug || c.categorySlug === categoryIdOrSlug) &&
        (!subCategoryIdOrSlug || c.subCategoryId === subCategoryIdOrSlug || c.subCategorySlug === subCategoryIdOrSlug)
    ) ?? null
  )
}

/**
 * Traverse all contents and call the callback function.
 * @param callback
 */
export async function traverseContents(
  callback: (context: {
    content: Content
    subCategory: SubCategory
    category: Category
    mainCategory: MainCategory
  }) => Promise<void>
): Promise<void> {
  console.debug('Traversing all contents...')
  for (const mainCategory of (await getData()).mainCategories) {
    const categories = await getCategories({ mainCategoryIdOrSlug: mainCategory.id })
    console.debug('Traversing Main category:', mainCategory.slug, 'categories:', categories.length)
    for (const category of categories) {
      const subCategories = await getSubCategories({
        mainCategoryIdOrSlug: mainCategory.id,
        categoryIdOrSlug: category.id,
      })
      console.debug('Traversing..Category:', category.slug, 'subCategories:', subCategories.length)
      for (const subCategory of subCategories) {
        const contents = await getContents({
          mainCategoryIdOrSlug: mainCategory.id,
          categoryIdOrSlug: category.id,
          subCategoryIdOrSlug: subCategory.id,
        })
        console.debug('Traversing....SubCategory:', subCategory.slug, 'contents:', contents.length)
        for (const content of contents) {
          console.debug('Traversing......Content:', content.slug)
          await callback({
            content,
            subCategory,
            category,
            mainCategory,
          })
        }
      }
    }
  }
  console.debug('Traversing all contents... Done')
}

/**
 * Determine if the main category is an audio category.
 * // TODO: Make configurable
 */
export const isAudioMainCategory = (mainCategorySlug: string) => mainCategorySlug === 'sfx'

/**
 * Validate the main category slug.
 * If the slug is not valid, return undefined.
 * @param mainCategorySlug
 */
export const validateMainCategorySlug = async (mainCategorySlug: string) => {
  const mainCategory = (await getData()).mainCategories.find((v) => v.slug === mainCategorySlug)
  if (!mainCategory) {
    return undefined
  }
  return mainCategory.slug
}
